import type { BossDef } from './boss-base';
import type { PatternDef } from '@/patterns/dsl';

/**
 * Geometric boss — "Prism" (def + tuning; behaviour in prism.ts). A
 * crystalline hexagon that paints rotating LATTICES the player weaves
 * through. Five faces are MIRRORS that refract shots into stray shards; one
 * LENS face is open — it steps around to look at the ship (the next face
 * flickers first) and fires refraction bolts that split mid-air. Damage
 * only lands through the lens. docs/02 + B1/B3.
 * Signatures: lens + refraction (always), counter-rotating LATTICE pair.
 */
export const PRISM: BossDef = {
  id: 'prism',
  nameKey: 'boss.prism',
  tier: 2,
  minRoom: 2,
  baseHp: 1000, // the lens gate costs uptime — a lighter hull keeps TTK
  radius: 52,
  bodyKey: 'boss/prism/body',
  movement: 'orbit',
  contactDamage: 1,
  phases: [
    {
      // Phase 1: slow counter-rotating hex spokes — a turning lattice
      hpPct: 1.0,
      patterns: [
        {
          type: 'radial', count: 6, interval: 0.5, spinDeg: 7,
          bullet: 'bullet/enemy/shard', speed: 120, radius: 10, damage: 1,
        },
        {
          type: 'radial', count: 6, interval: 0.5, spinDeg: -7, phaseDeg: 30,
          bullet: 'bullet/enemy/ring', speed: 120, radius: 10, damage: 2,
        },
      ],
    },
    {
      // Phase 2: tighter 8-spoke counter-net + a slow spiral threading it
      hpPct: 0.62,
      patterns: [
        {
          type: 'radial', count: 8, interval: 0.55, spinDeg: 6,
          bullet: 'bullet/enemy/shard', speed: 135, radius: 10, damage: 1,
        },
        {
          type: 'radial', count: 8, interval: 0.55, spinDeg: -6, phaseDeg: 22.5,
          bullet: 'bullet/enemy/ring', speed: 135, radius: 10, damage: 2,
        },
        {
          type: 'spiral', arms: 2, interval: 0.16, angularSpeed: 70, unlockTier: 1,
          bullet: 'bullet/enemy/shard', speed: 150, radius: 9, damage: 1,
        },
        {
          type: 'aimed', count: 3, spreadDeg: 20, interval: 2.4, telegraph: 0.4,
          unlockTier: 2,
          bullet: 'bullet/enemy/ring', speed: 240, radius: 10, damage: 2,
        },
      ],
    },
    {
      // Phase 3 rage: expanding star-burst — fast 5-point geometry + spiral
      hpPct: 0.28,
      speedMul: 1.35,
      patterns: [
        {
          type: 'radial', count: 5, interval: 0.32, spinDeg: 26,
          bullet: 'bullet/enemy/ring', speed: 170, radius: 10, damage: 2,
        },
        {
          type: 'spiral', arms: 3, interval: 0.15, angularSpeed: 95, unlockTier: 2,
          bullet: 'bullet/enemy/shard', speed: 175, radius: 9, damage: 1,
        },
      ],
    },
  ],
  evolve: [
    {
      // Refraction beam: a facet glows, then a tight ring-bullet fan
      tier: 2,
      pattern: {
        type: 'aimed', count: 3, spreadDeg: 14, interval: 3.2,
        telegraph: 0.5,
        bullet: 'bullet/enemy/ring', speed: 260, radius: 10, damage: 2,
      },
    },
    {
      // Square bloom: a slow 4-point star turning against the net
      tier: 3,
      fromPhase: 1,
      pattern: {
        type: 'radial', count: 4, interval: 1.7, spinDeg: -45,
        bullet: 'bullet/enemy/shard', speed: 185, radius: 10, damage: 1,
      },
    },
  ],
};

/**
 * Lens volleys — fire only while the open face looks at the ship. The bolt
 * refracts: it splits into three shards after a beat (docs/02 B3 split).
 */
export const LENS_PATTERNS: ReadonlyArray<PatternDef> = [
  {
    type: 'aimed', count: 1, spreadDeg: 0, interval: 2.2, telegraph: 0.45,
    bullet: 'bullet/enemy/lens-bolt', speed: 250, radius: 11, damage: 1,
    motion: {
      split: { after: 0.55, count: 3, spreadDeg: 50, speed: 230, radius: 9, bullet: 'bullet/enemy/shard' },
    },
  },
  {
    type: 'aimed', count: 3, spreadDeg: 18, interval: 2.9, telegraph: 0.45, unlockTier: 2,
    bullet: 'bullet/enemy/shard', speed: 240, radius: 10, damage: 1,
  },
];

/** Prism tuning — lens stepping, refraction, facet read-out. */
export const PR = {
  faces: 6,
  /** Lens hit wedge half-width (rad): the drawn face is ±30°, hits get a
   *  forgiving ±43° so shots grazing the lens rim still count */
  lensHalf: 0.75,
  /** Seconds between lens steps (real time); the next face flickers first */
  stepTime: 1.1,
  flicker: 0.45,
  /** Mirrored faces refract a hit into two stray shards (cooldown caps it) */
  refractCd: 0.25,
  refractSpread: 0.9,
  refractSpeed: 150,
  refractLife: 1.4,
  refractRadius: 8,
  /** Facet overlay: hexagon (vertex radius) just outside the body art */
  overlayR: 58,
  mirror: 0xf4f0ff,
  lens: 0xffb347,
  lensDark: 0x1a1030,
} as const;
